// Copyright © 2019-2026 Focal Systems. All rights reserved.

/**
 * Sections whose detail pages are siblings sharing one tab set, so the tab you're reading
 * survives a jump from one entity to the next (searching a store from a store's Settings tab
 * lands on the new store's Settings tab instead of dropping you back on Info).
 */
const CARRYOVER_SECTIONS = ["chains", "stores"] as const;

export type CarryoverSection = (typeof CARRYOVER_SECTIONS)[number];

export type CarriedTab = { section: CarryoverSection; tab: string };

/* A single trailing segment only — `/stores/compare/a/b` is not a detail page. */
const DETAIL_PATH = new RegExp(`^/(${CARRYOVER_SECTIONS.join("|")})/([^/]+)/?$`);

/**
 * The tab a jump between siblings should carry, or undefined when there's nothing to carry:
 * we're not on a chain/store detail page, or we're on its default (Info) tab.
 */
export function readCarriedTab(pathname: string, search: unknown): CarriedTab | undefined {
  const section = DETAIL_PATH.exec(pathname)?.[1] as CarryoverSection | undefined;
  if (!section) return undefined;
  const tab = (search as { t?: unknown } | null | undefined)?.t;
  if (typeof tab !== "string" || tab === "" || tab === "info") return undefined;
  return { section, tab };
}

/** Search params for a jump into `section` — the carried tab only applies within its own section. */
export function carriedSearch(carried: CarriedTab | undefined, section: CarryoverSection) {
  return carried?.section === section ? { t: carried.tab } : undefined;
}
